<script setup lang="ts">
  import { onMounted, onUnmounted, inject } from 'vue';
  import { ColoredButton, TableTitle } from '@/shared/ui';
  import orderIcon from '@/shared/assets/icons/Order.svg';
  import filterIcon from '@/shared/assets/icons/Filter.svg';
  import filterWhite from '@/shared/assets/icons/FilterWhite.svg';
  import orderWhite from '@/shared/assets/icons/OrderWhite.svg';
  import type { OrderOptions } from '@/shared/lib';
  import type { HeaderEmits } from '@/shared/lib';
  import { useFilterAndSort } from '@/shared/lib';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';

  interface Props {
    text: string;
    hasFilter?: boolean;
    isFilterActive?: boolean;
    orderOptions?: OrderOptions[];
    orderOptionClass?: string;
    boldTitle?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    hasFilter: true,
    isFilterActive: false,
    orderOptions: () => [],
    orderOptionClass: 'font-medium',
    boldTitle: true,
  });

  const emit = defineEmits<HeaderEmits>();

  const sortOrderState = inject(SortOrderInjectionKey, null);

  const {
    isOrderSelected,
    isOrderDropdownOpen,
    selectedOption,
    dropdownPosition,
    orderButtonRef,
    handleOrder,
    handleFilter,
    sortOptions,
    handleClickOutside,
    hideModalOverlay,
  } = useFilterAndSort(emit, sortOrderState);

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    sortOptions(sortCamp, orderFlow);
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    hideModalOverlay();
  });
</script>

<template>
  <th class="p-0 bg-white-01 border-b border-gray-02">
    <div class="w-full px-6 py-3 flex justify-between items-center text-gray-07">
      <TableTitle :text="text" :bold="boldTitle" />
      <div class="flex gap-4 relative items-center">
        <template v-if="$slots['extra-controls']">
          <slot name="extra-controls" />
        </template>
        <div class="relative">
          <div ref="orderButtonRef">
            <ColoredButton
              v-if="isOrderSelected"
              text="Ordenar por"
              :icon="orderWhite"
              icon-alt="Ícone de ordenação"
              :outlined="false"
              @click="handleOrder"
            />
            <ColoredButton
              v-else
              text="Ordenar por"
              :icon="orderIcon"
              icon-alt="Ícone de ordenação"
              :outlined="true"
              @click="handleOrder"
            />
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform opacity-0"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="transform opacity-0"
          >
            <div
              v-if="isOrderDropdownOpen"
              id="order-dropdown"
              class="fixed w-72 p-4 bg-white-01 rounded-2xl shadow-2xl z-50"
              :style="{ top: dropdownPosition.top + 'px', right: dropdownPosition.right + 'px' }"
            >
              <div class="py-1">
                <button
                  v-for="option in orderOptions"
                  :key="option.label"
                  class="block w-full text-left px-4 py-2 text-md text-gray-07"
                  :class="[
                    orderOptionClass,
                    {
                      'bg-gray-02 rounded-lg':
                        selectedOption?.sort === option.sort && selectedOption?.order === option.order,
                    },
                  ]"
                  @click="handleSortOptions(option.sort, option.order)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </transition>
        </div>
        <div v-if="hasFilter">
          <ColoredButton
            v-if="isFilterActive"
            text="Filtrar"
            :icon="filterWhite"
            icon-alt="Ícone de filtro"
            :outlined="false"
            @click="handleFilter"
          />
          <ColoredButton
            v-else
            text="Filtrar"
            :icon="filterIcon"
            icon-alt="Ícone de filtro"
            :outlined="true"
            @click="handleFilter"
          />
        </div>
      </div>
    </div>
  </th>
</template>
